import { getActiveBoard } from '../../board-context.js';
import { formatBeginnerPinLabel } from '../pins/pin-selector.js';

export type TftSpiUi =
  | {
      mode: 'skyrover-fixed';
      label: string;
      mosi: string;
      miso: string;
      sck: string;
      cs: string;
      dc: string;
      reset: string;
    }
  | {
      mode: 'esp-selectable';
    }
  | {
      mode: 'hardware-spi';
      /** Fixed hardware SPI bus pins, e.g. "MOSI-D11 | MISO-D12 | SCK-D13". */
      busLabel: string;
      mosi: string;
      miso: string;
      sck: string;
    };

function pinLabel(pin: string | number): string {
  return formatBeginnerPinLabel(pin, getActiveBoard());
}

function busLabel(mosi: string | number, miso: string | number, sck: string | number): string {
  return `MOSI-${pinLabel(mosi)} | MISO-${pinLabel(miso)} | SCK-${pinLabel(sck)}`;
}

/**
 * Board-aware SPI pin UI for Configure TFT.
 * Skyrover: fixed pins. ESP32: all selectable. Other boards: hardware SPI + CS/DC/RESET.
 */
export function getTftSpiUi(boardId = getActiveBoard()?.id ?? ''): TftSpiUi {
  const id = boardId.toLowerCase();
  const board = getActiveBoard();
  const spi = board?.pins?.spi;

  if (id === 'skyrover-esp32-s3') {
    return {
      mode: 'skyrover-fixed',
      label:
        'MOSI-GPIO17 | MISO-GPIO18 | SCK-GPIO16 | RESET-GPIO5 | DC-GPIO6 | CS-GPIO7',
      mosi: '17',
      miso: '18',
      sck: '16',
      reset: '5',
      dc: '6',
      cs: '7',
    };
  }

  // ESP32 DevKit (and other ESP32 boards): student picks every pin.
  if (id.includes('esp32')) {
    return { mode: 'esp-selectable' };
  }

  // Arduino / ESP8266 / others: hardware SPI bus is fixed; CS, DC, RESET are choosable.
  if (spi?.mosi != null && spi?.miso != null && spi?.sck != null) {
    return {
      mode: 'hardware-spi',
      busLabel: busLabel(spi.mosi, spi.miso, spi.sck),
      mosi: String(spi.mosi),
      miso: String(spi.miso),
      sck: String(spi.sck),
    };
  }

  // Uno-like fallback
  return {
    mode: 'hardware-spi',
    busLabel: busLabel(11, 12, 13),
    mosi: '11',
    miso: '12',
    sck: '13',
  };
}
